import { useEffect, useMemo, useRef, useState } from 'react';
import { Typography } from 'antd';
import {
  clearGoogleMapsInstanceListeners,
  createGoogleMapsMarker,
  type GoogleAdvancedMarkerInstance,
  type GoogleMapInstance,
  type GoogleMarkerInstance,
  type GoogleMapsNamespace,
  loadGoogleMapsScript,
  readGoogleMapsMarkerPosition,
  withGoogleMapsMapId,
} from '~/app/lib/google-maps';

const { Text } = Typography;

type Props = {
  latitude: number | null;
  longitude: number | null;
  title: string;
  unavailableText: string;
  missingKeyText: string;
  onCoordinatesChange?: (coords: { lat: number; lng: number }) => void;
  /** When true, map fills its parent instead of using the default min-height. */
  fillContainer?: boolean;

  showZoomControl?: boolean;
  className?: string;
};

function triggerMapResize(
  googleMaps: GoogleMapsNamespace,
  map: GoogleMapInstance,
  center: { lat: number; lng: number } | null,
): void {
  googleMaps.maps.event.trigger(map, 'resize');
  if (center) {
    map.setCenter(center);
  }
}

export function AssetLocationMap({
  latitude,
  longitude,
  title,
  unavailableText,
  missingKeyText,
  onCoordinatesChange,
  fillContainer = false,
  showZoomControl = true,
  className,
}: Props) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const markerRef = useRef<
    GoogleAdvancedMarkerInstance | GoogleMarkerInstance | null
  >(null);
  const mapRef = useRef<GoogleMapInstance | null>(null);
  const googleMapsRef = useRef<GoogleMapsNamespace | null>(null);
  const onCoordinatesChangeRef = useRef(onCoordinatesChange);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  onCoordinatesChangeRef.current = onCoordinatesChange;

  const coordinates = useMemo(() => {
    if (latitude === null || longitude === null) {
      return null;
    }

    if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
      return null;
    }

    return {
      lat: latitude,
      lng: longitude,
    };
  }, [latitude, longitude]);

  useEffect(() => {
    if (!coordinates || !mapContainerRef.current) {
      return;
    }

    let isCancelled = false;

    void loadGoogleMapsScript()
      .then((googleMaps) => {
        if (isCancelled || !mapContainerRef.current) {
          return;
        }

        googleMapsRef.current = googleMaps;
        setErrorMessage(null);

        if (!mapRef.current) {
          mapRef.current = new googleMaps.maps.Map(
            mapContainerRef.current,
            withGoogleMapsMapId({
              center: coordinates,
              zoom: 14,
              disableDefaultUI: false,
              fullscreenControl: showZoomControl,
              mapTypeControl: false,
              streetViewControl: false,
              zoomControl: showZoomControl,
            }),
          );

          markerRef.current = createGoogleMapsMarker(googleMaps, {
            map: mapRef.current,
            position: coordinates,
            title,
            draggable: true,
          });

          markerRef.current.addListener?.('dragend', () => {
            const marker = markerRef.current;
            if (!marker) {
              return;
            }

            const newPosition = readGoogleMapsMarkerPosition(marker);

            if (newPosition) {
              onCoordinatesChangeRef.current?.(newPosition);
            }
          });

          setIsReady(true);
          requestAnimationFrame(() => {
            if (!isCancelled && mapRef.current && googleMapsRef.current) {
              triggerMapResize(
                googleMapsRef.current,
                mapRef.current,
                coordinates,
              );
            }
          });
          return;
        }

        mapRef.current.setCenter(coordinates);

        if (markerRef.current) {
          if ('setPosition' in markerRef.current) {
            markerRef.current.setPosition(coordinates);
          } else {
            markerRef.current.position = coordinates;
          }
        }
      })
      .catch((error: Error) => {
        if (isCancelled) {
          return;
        }

        setIsReady(false);
        setErrorMessage(
          error.message === 'Missing VITE_PUBLIC_GOOGLE_MAPS_API_KEY'
            ? missingKeyText
            : error.message,
        );
      });

    return () => {
      isCancelled = true;
    };
  }, [coordinates, missingKeyText, showZoomControl, title]);

  useEffect(() => {
    return () => {
      clearGoogleMapsInstanceListeners(markerRef.current);
      clearGoogleMapsInstanceListeners(mapRef.current);
      markerRef.current = null;
      mapRef.current = null;
      googleMapsRef.current = null;
    };
  }, []);

  useEffect(() => {
    const container = mapContainerRef.current;
    const map = mapRef.current;
    const googleMaps = googleMapsRef.current;

    if (!container || !map || !googleMaps || !isReady) {
      return;
    }

    const observer = new ResizeObserver(() => {
      triggerMapResize(googleMaps, map, coordinates);
    });

    observer.observe(container);
    triggerMapResize(googleMaps, map, coordinates);

    return () => {
      observer.disconnect();
    };
  }, [coordinates, isReady, errorMessage]);

  const containerStyle = {
    width: '100%',
    height: fillContainer ? '100%' : undefined,
    minHeight: fillContainer ? '100%' : 280,
    borderRadius: 8,
    background: 'var(--ant-colorFillAlter, #f8fafc)',
  } as const;

  if (!coordinates) {
    return (
      <div
        className={className}
        style={{
          ...containerStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          textAlign: 'center',
          boxSizing: 'border-box',
        }}
      >
        <Text type="secondary">{unavailableText}</Text>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div
        className={className}
        style={{
          ...containerStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          textAlign: 'center',
          boxSizing: 'border-box',
        }}
      >
        <Text type="secondary">{errorMessage}</Text>
      </div>
    );
  }

  return (
    <div
      ref={mapContainerRef}
      className={className}
      style={{
        ...containerStyle,
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    />
  );
}
